
import React, { useState } from 'react';
import { Mail, Download } from 'lucide-react';
import { apiClient } from '../../apiClient';
import { useAuth } from '../../firebase';

const EmailMagazine = () => {
    const currentUser = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const downloadCsv = async (category) => {
        setLoading(true);
        setError('');
        try {
            const token = await currentUser.getIdToken();
            const response = await apiClient.get(`/v1/admin/email-magazine/export?category=${category}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to export CSV');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `email_magazine_${category}_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Export error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const categories = [
        { id: 'importantNotice', label: '重要なお知らせ', description: 'サービス利用上重要な案内やセキュリティ関連通知の受信者' },
        { id: 'maintenanceNotice', label: '障害・メンテナンス情報', description: '障害発生、復旧、計画メンテナンスのお知らせの受信者' },
        { id: 'signalNews', label: 'シグナルからのお知らせ', description: '新機能、改善、キャンペーンのお知らせの受信者' }
    ];

    return (
        <div>
            <div className="flex items-center mb-8 pb-4 border-b">
                <Mail className="w-8 h-8 text-blue-600 mr-3" />
                <h1 className="text-2xl font-bold text-gray-800">メールマガジン配信対象エクスポート</h1>
            </div>

            <p className="text-gray-600 mb-8">
                各通知設定で「オン」にしているユーザーのリストをCSVでダウンロードできます。<br />
                法人登録企業と個人登録で出力される項目が異なります。
            </p>

            {error && (
                <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-100 flex items-center">
                    <span className="mr-2">⚠️</span>
                    {error}
                </div>
            )}

            <div className="grid gap-6">
                {categories.map((cat) => (
                    <div key={cat.id} className="bg-white border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between">
                        <div className="flex-1 pr-6">
                            <h3 className="text-lg font-bold text-gray-800 mb-1">{cat.label}</h3>
                            <p className="text-sm text-gray-500">{cat.description}</p>
                        </div>
                        <button
                            onClick={() => downloadCsv(cat.id)}
                            disabled={loading}
                            className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors font-medium"
                        >
                            <Download className="w-5 h-5 mr-2" />
                            {loading ? 'エクスポート中...' : 'CSVダウンロード'}
                        </button>
                    </div>
                ))}
            </div>

            <div className="mt-12 bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h4 className="font-bold text-gray-700 mb-3">出力項目の詳細</h4>
                <div className="grid md:grid-cols-2 gap-8 text-sm">
                    <div>
                        <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-bold mb-2">法人登録 (1...)</span>
                        <ul className="list-disc list-inside text-gray-600 space-y-1">
                            <li>メールアドレス (email)</li>
                            <li>企業名 (companyName)</li>
                            <li>部署名 (departmentName)</li>
                            <li>名前 (representativeName)</li>
                        </ul>
                    </div>
                    <div>
                        <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-bold mb-2">個人登録 (2...)</span>
                        <ul className="list-disc list-inside text-gray-600 space-y-1">
                            <li>メールアドレス (email)</li>
                            <li>名前 (representativeName)</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailMagazine;
